import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { C } from "../reporting/reportingStyles";
import { Badge } from "../reporting/ReportingShared";
import AuditLogPanel from "../common/AuditLogPanel";
import { successToast } from "../../shared/Toast";
import { sendTestCampaign } from "../../utils/pushCampaignApi";
import {
    STATUS_META,
    SCREEN_LABELS,
    describeAudience,
    CAMPAIGN_AUDIT_ACTION_LABELS,
    CAMPAIGN_AUDIT_FIELD_LABELS,
    campaignAuditResolve,
} from "../../constants/pushCampaigns";

/**
 * Read-only view of one campaign: what was sent, to whom, how it went, and its history.
 * Mutations (edit / cancel / duplicate) live on the list page; this modal only offers a
 * test send, which never changes the campaign.
 */

const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, color: C.text, background: C.white, boxSizing: "border-box",
};
const btnSecondary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
    background: C.white, color: C.textMed, border: `1px solid ${C.border}`,
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const Stat = ({ label, value, sub }) => (
    <div style={{ padding: "12px 14px", borderRadius: 10, background: C.bg, minWidth: 110 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginTop: 4 }}>{value}</div>
        {sub ? <div style={{ fontSize: 11, color: C.textMed }}>{sub}</div> : null}
    </div>
);

const Row = ({ label, children }) => (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 12, padding: "8px 0", borderBottom: `1px solid ${C.borderLight}`, fontSize: 13 }}>
        <div style={{ color: C.textLight, fontWeight: 600 }}>{label}</div>
        <div style={{ color: C.text, minWidth: 0, wordBreak: "break-word" }}>{children}</div>
    </div>
);

const CampaignDetailsModal = ({ isOpen, onClose, campaign, merchantId, readOnly }) => {
    const [tab, setTab] = useState("summary");
    const [testPhone, setTestPhone] = useState("");
    const [testing, setTesting] = useState(false);

    if (!campaign) return null;
    const meta = STATUS_META[campaign.status] || { label: campaign.status, color: C.textMed, bg: C.borderLight };
    const openRate = campaign.sentCount > 0 ? Math.round((campaign.openedCount / campaign.sentCount) * 100) : null;

    const handleTest = async () => {
        setTesting(true);
        try {
            const r = await sendTestCampaign(campaign.id, testPhone.trim());
            if (r) successToast(r.message || `Test sent to ${r.sent} device(s)`);
        } finally {
            setTesting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>
                <span style={{ marginRight: 10 }}>{campaign.name}</span>
                <Badge color={meta.color} bg={meta.bg}>{meta.label}</Badge>
            </ModalHeader>
            <ModalBody>
                <Nav tabs style={{ borderBottom: `1px solid ${C.border}` }}>
                    <NavItem>
                        <NavLink active={tab === "summary"} onClick={() => setTab("summary")} style={{ cursor: "pointer", color: tab === "summary" ? C.coral : C.textMed, fontWeight: 600 }}>Summary</NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink active={tab === "history"} onClick={() => setTab("history")} style={{ cursor: "pointer", color: tab === "history" ? C.coral : C.textMed, fontWeight: 600 }}>History</NavLink>
                    </NavItem>
                </Nav>
                <TabContent activeTab={tab} style={{ paddingTop: 16 }}>
                    <TabPane tabId="summary">
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                            <Stat label="Customers" value={campaign.targetedUsers} sub="targeted" />
                            <Stat label="Devices" value={campaign.targetedTokens} sub="targeted" />
                            <Stat label="Delivered" value={campaign.sentCount} sub={`${campaign.failedCount} failed · ${campaign.prunedCount} dead removed`} />
                            <Stat label="Opened" value={campaign.openedCount} sub={openRate === null ? "—" : `${openRate}% of delivered`} />
                        </div>

                        <Row label="Title (EN)">{campaign.titleEn}</Row>
                        <Row label="Body (EN)">{campaign.bodyEn}</Row>
                        {campaign.titleAr ? <Row label="Title (AR)"><span dir="rtl">{campaign.titleAr}</span></Row> : null}
                        {campaign.bodyAr ? <Row label="Body (AR)"><span dir="rtl">{campaign.bodyAr}</span></Row> : null}
                        <Row label="Image">{campaign.imageUrl ? <img src={campaign.imageUrl} alt="" style={{ maxWidth: 240, maxHeight: 140, borderRadius: 8 }} /> : <em style={{ color: C.textLight }}>none</em>}</Row>
                        <Row label="Audience">{describeAudience(campaign.audience)}</Row>
                        <Row label="App">{campaign.app ? (campaign.app.name || campaign.app.reference) : "All linked apps"}</Row>
                        <Row label="Opens">{campaign.deepLink?.screen === "product" ? `Product #${campaign.deepLink.productId}` : SCREEN_LABELS[campaign.deepLink?.screen] || "Home"}</Row>
                        <Row label="Created">{fmt(campaign.createdAt)} {campaign.createdBy ? <span style={{ color: C.textLight }}>by {campaign.createdBy.email || `${campaign.createdBy.firstName || ""} ${campaign.createdBy.lastName || ""}`.trim()}</span> : null}</Row>
                        <Row label="Scheduled">{fmt(campaign.scheduledAt)}</Row>
                        <Row label="Started">{fmt(campaign.startedAt)}</Row>
                        <Row label="Finished">{fmt(campaign.finishedAt)}</Row>
                        {campaign.lastError ? (
                            <Row label="Note">
                                <span style={{ color: campaign.status === "failed" ? C.coral : C.orange }}>{campaign.lastError}</span>
                            </Row>
                        ) : null}

                        {!readOnly && (
                            <div style={{ marginTop: 16, padding: 12, border: `1px dashed ${C.border}`, borderRadius: 12 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>Send a test to a phone</div>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <input style={inputStyle} value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Customer phone number (signed in to the app)" />
                                    <button type="button" onClick={handleTest} disabled={testing} style={{ ...btnSecondary, whiteSpace: "nowrap", opacity: testing ? 0.7 : 1 }}>
                                        {testing ? "Sending…" : "📲 Send test"}
                                    </button>
                                </div>
                                <div style={{ fontSize: 11, color: C.textLight, marginTop: 5 }}>Only that customer's devices; nothing else changes.</div>
                            </div>
                        )}
                    </TabPane>
                    <TabPane tabId="history">
                        {tab === "history" && (
                            <AuditLogPanel
                                merchantId={merchantId}
                                module="marketing"
                                entityType="push_campaign"
                                entityId={campaign.id}
                                title="📜 What happened to this campaign"
                                actionLabels={CAMPAIGN_AUDIT_ACTION_LABELS}
                                fieldLabels={CAMPAIGN_AUDIT_FIELD_LABELS}
                                resolve={campaignAuditResolve}
                                defaultLimit={20}
                            />
                        )}
                    </TabPane>
                </TabContent>
            </ModalBody>
            <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                <button type="button" onClick={onClose} style={btnSecondary}>Close</button>
            </ModalFooter>
        </Modal>
    );
};

export default CampaignDetailsModal;
